/**
 * Parameters for the {@link leftJoin} function.
 *
 * @template L - Type of items in the left array.
 * @template R - Type of items in the right array.
 * @template LK - Key of `L` whose value is used to match against the right array.
 * @template RK - Key of `R` whose value is used as the lookup key.
 * @template LD - Key of `L` where the matched right row will be stored.
 */
interface LeftJoinParams<L extends Record<string, any>, R extends Record<string, any>, LK extends keyof L, RK extends keyof R, LD extends keyof L> {
  /** The left (primary) array. Each of its items will be mutated to include the matched right item. */
  left: L[] | null | undefined;
  /** The right (lookup) array whose rows are matched into the left array. */
  right: R[] | null | undefined;
  /** Property name on left items used to find a match in the right array. */
  leftKey: LK;
  /** Property name on right items used as the lookup key. */
  rightKey: RK;
  /** Property name on left items where the matched right item will be assigned. */
  leftDest: LD;
}

/**
 * Performs an in-place left join of two arrays of objects.
 *
 * For every row in `left`, the function looks up a row in `right` whose
 * `rightKey` equals the left row's `leftKey`, and assigns the found row
 * (or `undefined` if no match exists) to the `leftDest` property of the
 * left row.
 *
 * If either `left` or `right` is `null`/`undefined`, the function is a no-op.
 *
 * Note: this mutates the items in `left`.
 *
 * @param params - The join parameters. See {@link LeftJoinParams}.
 *
 * @example
 * const users = [{ id: 1, roleId: 10 }, { id: 2, roleId: 20 }];
 * const roles = [{ id: 10, name: 'admin' }, { id: 20, name: 'user' }];
 * leftJoin({
 *   left: users,
 *   right: roles,
 *   leftKey: 'roleId',
 *   rightKey: 'id',
 *   leftDest: 'role',
 * });
 * // users[0].role === { id: 10, name: 'admin' }
 */
export function leftJoin<L extends Record<string, any>, R extends Record<string, any>, LK extends keyof L, RK extends keyof R, LD extends keyof L>({
                                                                                                                                                     left,
                                                                                                                                                     right,
                                                                                                                                                     leftKey,
                                                                                                                                                     rightKey,
                                                                                                                                                     leftDest,
                                                                                                                                                   }: LeftJoinParams<L, R, LK, RK, LD>): void {
  if (left && right) {
    const map: Record<string, R> = {};
    for (const row of right) {
      map[String(row[rightKey])] = row;
    }

    for (const row of left) {
      (row[leftDest] as unknown as R | undefined) = map[String(row[leftKey])];
    }
  }
}
